// src/pages/auth/AcceptInvitePage.tsx

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Home as HomeIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";

import { authService } from "../../services/authService";
import { useApplicationManager } from "../../contexts/ApplicationManagerContext";

// Constantes de tema
import { 
  VERDE_MENTA, 
  VERDE_MENTA_ESCURO, 
  FUNDO_ESCURO,
  FUNDO_PAPEL,
  TEXTO_PRIMARIO,
  TEXTO_SECUNDARIO,
  withAlpha
} from "../../themes/themeConstants";

interface InviteData {
  email: string;
  nomeSugerido: string;
  projetoNome: string;
  papel: string;
  criadoPor: string;
  mensagem: string;
  userExists: boolean;
  expiresAt: string;
}

const AcceptInvitePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, initialize } = useApplicationManager();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const token = searchParams.get("token");

  // Validação dos dados do convite
  useEffect(() => {
    const validateInvite = async () => {
      if (!token) {
        setError("Token de convite não fornecido");
        setLoading(false);
        return;
      }

      try {
        const response = await authService.validateInviteToken(token);
        
        if (response.success) {
          setInviteData(response.data);
        } else {
          setError(response.message || "Convite inválido");
        }
      } catch (err: any) {
        setError(err.response?.data?.message || "Erro ao validar convite");
      } finally {
        setLoading(false);
      }
    };

    validateInvite();
  }, [token]);

  // Schema de validação
  const validationSchema = yup.object({
    name: yup.string().required("Nome é obrigatório"),
    password: yup.string()
      .min(8, "A senha deve ter pelo menos 8 caracteres")
      .required("Senha é obrigatória"),
    confirmPassword: yup.string()
      .oneOf([yup.ref("password")], "As senhas devem ser iguais")
      .required("Confirmação de senha é obrigatória"),
  });

  const formik = useFormik({
    initialValues: {
      name: inviteData?.nomeSugerido || "",
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!token) return;

      try {
        setSubmitting(true);
        setError(null);

        const response = await authService.acceptInvite(token, {
          name: values.name,
          password: values.password,
        });

        if (response.success) {
          // Atualizar contexto de autenticação
          await initialize();
          
          // Redirecionar para o dashboard
          navigate("/dashboard", { replace: true });
        } else {
          setError(response.message || "Erro ao aceitar convite");
        }
      } catch (err: any) {
        setError(err.response?.data?.message || "Erro ao aceitar convite");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Atualizar o valor userExists quando inviteData mudar
  useEffect(() => {
    if (inviteData) {
      formik.setFieldValue("userExists", inviteData.userExists);
      formik.setFieldValue("name", inviteData.nomeSugerido);
    }
  }, [inviteData]);

  // Renderizar loading
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: FUNDO_ESCURO,
        }}
      >
        <CircularProgress sx={{ color: VERDE_MENTA }} />
      </Box>
    );
  }

  // Renderizar erro se não tiver dados do convite
  if (!inviteData) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: FUNDO_ESCURO,
        }}
      >
        <Card
          sx={{
            maxWidth: 400,
            backgroundColor: FUNDO_PAPEL,
            border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
          }}
        >
          <CardContent sx={{ textAlign: "center", p: 4 }}>
            <Typography variant="h6" sx={{ color: TEXTO_PRIMARIO, mb: 2 }}>
              Convite Inválido
            </Typography>
            <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO, mb: 3 }}>
              {error || "Este convite não é válido ou já expirou."}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/login")}
              sx={{
                backgroundColor: VERDE_MENTA,
                "&:hover": { backgroundColor: VERDE_MENTA_ESCURO },
              }}
            >
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: FUNDO_ESCURO,
      }}
    >
      {/* Botão Home */}
      <Box sx={{ position: "absolute", top: 20, left: 20 }}>
        <Tooltip title="Voltar para a página inicial">
          <IconButton
            onClick={() => navigate("/")}
            size="medium"
            sx={{
              color: VERDE_MENTA,
              background: withAlpha(VERDE_MENTA, 0.1),
              "&:hover": {
                background: withAlpha(VERDE_MENTA, 0.2),
              },
            }}
          >
            <HomeIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Card principal */}
      <Box
        sx={{
          width: { xs: "90%", sm: "500px" },
          maxWidth: "500px",
          backgroundColor: FUNDO_PAPEL,
          borderRadius: 3,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
          border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${VERDE_MENTA} 0%, ${VERDE_MENTA_ESCURO} 100%)`,
            p: 3,
            textAlign: "center",
          }}
        >
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mx: "auto",
              mb: 2,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            }}
          >
            <PersonAddIcon sx={{ color: "white", fontSize: 30 }} />
          </Avatar>
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 600,
              mb: 1,
            }}
          >
            Aceitar Convite
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
            }}
          >
            Complete seus dados para participar do projeto
          </Typography>
        </Box>

        {/* Informações do convite */}
        <Box sx={{ p: 3 }}>
          <Card
            sx={{
              backgroundColor: withAlpha(VERDE_MENTA, 0.05),
              border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
              mb: 3,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <EmailIcon sx={{ color: VERDE_MENTA, mr: 1 }} />
                <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO }}>
                  E-mail: <strong>{inviteData.email}</strong>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <BusinessIcon sx={{ color: VERDE_MENTA, mr: 1 }} />
                <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO }}>
                  Projeto: <strong>{inviteData.projetoNome}</strong>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon sx={{ color: VERDE_MENTA, mr: 1 }} />
                <Typography variant="body2" sx={{ color: TEXTO_SECUNDARIO }}>
                  Papel: <strong>{inviteData.papel}</strong>
                </Typography>
              </Box>
              {inviteData.mensagem && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: withAlpha(VERDE_MENTA, 0.1), borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: TEXTO_PRIMARIO, fontStyle: "italic" }}>
                    "{inviteData.mensagem}"
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Alerta de erro */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: "rgba(244, 67, 54, 0.15)",
                color: "#e57373",
                border: "1px solid rgba(244, 67, 54, 0.3)",
              }}
            >
              {error}
            </Alert>
          )}

          {/* Formulário */}
          <Box component="form" onSubmit={formik.handleSubmit}>
            {/* Sempre mostrar campos - usuário deve definir senha mesmo se já existir */}
            <>
              {/* Campo Nome */}
              <TextField
                  fullWidth
                  name="name"
                  label="Nome Completo"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#3A3A3A",
                      "& fieldset": { borderColor: "#555555" },
                      "&:hover fieldset": { borderColor: "#777777" },
                      "&.Mui-focused fieldset": { borderColor: VERDE_MENTA },
                    },
                    "& .MuiInputBase-input": { color: TEXTO_PRIMARIO },
                    "& .MuiInputLabel-root": { color: TEXTO_SECUNDARIO },
                    "& .MuiFormHelperText-root": { color: "#F44336" },
                  }}
                />

                {/* Campo Senha */}
                <TextField
                  fullWidth
                  name="password"
                  label="Senha"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#3A3A3A",
                      "& fieldset": { borderColor: "#555555" },
                      "&:hover fieldset": { borderColor: "#777777" },
                      "&.Mui-focused fieldset": { borderColor: VERDE_MENTA },
                    },
                    "& .MuiInputBase-input": { color: TEXTO_PRIMARIO },
                    "& .MuiInputLabel-root": { color: TEXTO_SECUNDARIO },
                    "& .MuiFormHelperText-root": { color: "#F44336" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: VERDE_MENTA }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Campo Confirmar Senha */}
                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="Confirmar Senha"
                  type="password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#3A3A3A",
                      "& fieldset": { borderColor: "#555555" },
                      "&:hover fieldset": { borderColor: "#777777" },
                      "&.Mui-focused fieldset": { borderColor: VERDE_MENTA },
                    },
                    "& .MuiInputBase-input": { color: TEXTO_PRIMARIO },
                    "& .MuiInputLabel-root": { color: TEXTO_SECUNDARIO },
                    "& .MuiFormHelperText-root": { color: "#F44336" },
                  }}
                />
              </>

            {/* Botões */}
            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate("/login")}
                sx={{
                  borderColor: VERDE_MENTA,
                  color: VERDE_MENTA,
                  "&:hover": {
                    borderColor: VERDE_MENTA_ESCURO,
                    backgroundColor: withAlpha(VERDE_MENTA, 0.1),
                  },
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                sx={{
                  backgroundColor: VERDE_MENTA,
                  "&:hover": { backgroundColor: VERDE_MENTA_ESCURO },
                }}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : inviteData.userExists ? (
                  "Confirmar"
                ) : (
                  "Aceitar Convite"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AcceptInvitePage;